import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('zouqai_token'));
  const [loading, setLoading] = useState(true);
  const [activeBranch, setActiveBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [businessType, setBusinessType] = useState(() => localStorage.getItem('zouqai_business_type') || 'restaurant');

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setUser(res.data);
          return axios.get(`${API}/branches`, { headers: { Authorization: `Bearer ${token}` } });
        })
        .then(res => {
          setBranches(res.data);
          if (res.data.length > 0 && !activeBranch) {
            setActiveBranch(res.data[0]);
          }
        })
        .catch(() => {
          localStorage.removeItem('zouqai_token');
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('zouqai_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem('zouqai_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const demoLogin = async (businessTypeOverride, businessNameOverride, priceLevel, city) => {
    const bt = businessTypeOverride || businessType;
    const payload = { business_type: bt };
    if (businessNameOverride) payload.business_name = businessNameOverride;
    if (priceLevel) payload.price_level = priceLevel;
    if (city) payload.city = city;
    const res = await axios.post(`${API}/auth/demo`, payload);
    localStorage.setItem('zouqai_token', res.data.token);
    localStorage.setItem('zouqai_demo', 'true');
    localStorage.setItem('zouqai_business_type', bt);
    setToken(res.data.token);
    setUser(res.data.user);
    setBusinessType(bt);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('zouqai_token');
    localStorage.removeItem('zouqai_demo');
    setToken(null);
    setUser(null);
    setBranches([]);
    setActiveBranch(null);
  };

  const isDemo = user?.is_demo || localStorage.getItem('zouqai_demo') === 'true';

  // RBAC helpers
  const userRole = user?.role || 'owner';
  const userPermissions = user?.permissions || ['*'];
  const hasPermission = useCallback((perm) => {
    if (userPermissions.includes('*')) return true;
    return userPermissions.includes(perm);
  }, [userPermissions]);

  const seedData = async () => {
    const res = await axios.post(`${API}/seed`, {}, authHeaders());
    const branchRes = await axios.get(`${API}/branches`, authHeaders());
    setBranches(branchRes.data);
    if (branchRes.data.length > 0) setActiveBranch(branchRes.data[0]);
    return res.data;
  };

  const refreshBranches = async () => {
    const res = await axios.get(`${API}/branches`, authHeaders());
    setBranches(res.data);
    if (res.data.length > 0 && !activeBranch) setActiveBranch(res.data[0]);
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, demoLogin, isDemo,
      activeBranch, setActiveBranch, branches, setBranches,
      seedData, refreshBranches, authHeaders, API,
      businessType, setBusinessType: (type) => { setBusinessType(type); localStorage.setItem('zouqai_business_type', type); },
      userRole, userPermissions, hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
